import { Component, computed, effect, signal, WritableSignal } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { currentTenantId } from '@/utils/admin-utils';
import { Validators } from '@angular/forms';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TenantRoleDetailDto, TenantRoleMergeDto, TenantRoleService, UserItemDto, } from '@artemis-software/wr-api';
import { createForm } from '@sonofabit/ng-core';
import { tryWithLoading } from '@/utils/async-utils';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import { PendingChanges } from '@guards/pending-changes.guard';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { checkRoleWeightValidator } from '@/utils/validators';
import { AssignmentType } from '@shared/form-controls/generic-pick-list/generic-pick-list.component';

@Component({
  standalone: false,
  selector: 'wr-tenant-role-detail-page',
  templateUrl: './tenant-role-detail-page.component.html',
  styleUrls: ['./tenant-role-detail-page.component.scss']
})
export class TenantRoleDetailPageComponent implements PendingChanges {
  isEditing = false;
  firstSubmit = false;
  tenantRoleId: string | null = null;
  form?: ReturnType<typeof this.createFormFromDetail>;
  currentTenantId = currentTenantId();
  readonly canEdit = signal(false);
  readonly isNew = computed(() => this.tenantRoleId && this.tenantRoleId === 'new');

  readonly config = asyncComputed(() => {
    const tenantId = this.currentTenantId();
    if (!tenantId)
      return null;
    return this.tenantRoleService.getCreateConfig(tenantId);
  });

  assignedUsers: WritableSignal<UserItemDto[]> = signal([]);
  role: WritableSignal<TenantRoleDetailDto | undefined> = signal(undefined);

  loading$ = new BehaviorSubject(false);

  constructor(private readonly router: Router,
    route: ActivatedRoute,
              private readonly dialogService: NbDialogService,
              private readonly tenantRoleService: TenantRoleService,
              private readonly nbToastrService: NbToastrService,) {
    route.paramMap.subscribe(async (params) => {
      const id = this.tenantRoleId = params.get('id');
      if (id) {
        if (id === 'new') {
          this.isEditing = true;
          this.canEdit.set(true);
          this.form = this.createFormFromDetail(null);
        } else {
          this.tenantRoleService.findById(id).subscribe((tenantRole) => {
            this.canEdit.set(tenantRole.permissions.canEdit);
            this.form = this.createFormFromDetail(tenantRole);
            this.form?.disable();
            this.role.set(tenantRole);
          });
        }
      } else {
        await this.router.navigate(['tenantRoles']);
      }
    });
    effect(() => {
      const role = this.role();
      const config = this.config();
      if(role && config) {
        this.assignedUsers.set(role.users);
      }
    });
  }

  readonly assignmentTypeFunction = (): AssignmentType => {
    return AssignmentType.UNASSIGNED;
  };

  createFormFromDetail(detail: TenantRoleDetailDto | null) {
    return createForm(({ field }) => {
      return {
        id: field({ value: detail?.id }),
        name: field({ value: detail?.name, validators: [Validators.required] }),
        description: field({ value: detail?.description, validators: [Validators.required] }),
        color: field({ value: detail?.color, validators: [Validators.required] }),
        weight: field({ value: detail?.weight, validators: [Validators.required],
          asyncValidators: [checkRoleWeightValidator.createValidator(this.tenantRoleService, detail?.id ?? '')] }),
        privileges: field({ value: detail?.privileges, validators: [Validators.required] }),
        users: field({ value: detail?.users }),
      };
    });
  }

  onEdit(): void {
    this.isEditing = true;
    this.form?.enable();
  }

  async submit(): Promise<void> {
    this.firstSubmit = true;
    this.form?.markAllAsTouched();
    this.form?.updateValueAndValidity({ onlySelf: false, emitEvent: false });

    if (!this.form?.valid) {
      this.nbToastrService.danger('Rolle kann nicht gespeichert werden, es gibt noch Fehler.', 'Rolle');
      return;
    }

    await tryWithLoading(this.loading$, async () => {
      const tenantRoleDto = {
        ...this.form?.value,
        users: this.form?.value.users?.map(user => user.id),
      } as TenantRoleMergeDto;
      const saved = await firstValueFrom(this.tenantRoleService.merge(tenantRoleDto));
      this.form?.get('id')?.setValue(saved.id);
      await this.reloadForm();
      await this.router.navigate(['tenantRole', saved.id], { replaceUrl: true });
      this.nbToastrService.success('Rolle wurde erfolgreich gespeichert', 'Rolle');
    });
  }

  async reloadForm(): Promise<void> {
    await tryWithLoading(this.loading$, async () => {
      const id = this.form?.value.id;
      if (id) {
        const tenantRole = await firstValueFrom(this.tenantRoleService.findById(id));
        this.form = this.createFormFromDetail(tenantRole);
      }
    });
    this.isEditing = false;
    this.form?.disable();
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['tenantRoles']);
  }

  hasPendingChanges(): boolean | Promise<boolean> {
    return this.isEditing;
  }

  async onDelete(): Promise<void> {
    if (await firstValueFrom(this.dialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Rolle löschen',
        message: 'Bist du sicher, dass du die Rolle löschen willst?',
      },
    }).onClose) && this.form?.value.id) {
      await firstValueFrom(this.tenantRoleService.deleteById(this.form?.value.id));
      await this.router.navigate(['tenantRoles']);
      this.nbToastrService.success('Rolle wurde erfolgreich gelöscht', 'Rolle');
    }
  }

  async cancel(): Promise<void> {
    await this.reloadForm();
  }
}
